import { EventLocationData, useHistorySearch } from '@eagle/react-common';
import L from 'leaflet';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Marker, useMap } from 'react-leaflet';
import { getEventColor, getEventLatLng } from '../trip-history-base-view-utils';
import { HoveredEventPulseOverlayProps } from '../trip-history-base-view.types';

export const HoveredEventPulseOverlay: FC<HoveredEventPulseOverlayProps> = ({ eventDefinitions, hoveredEventId, visibleJourneyIds, visibleTripId }) => {
  const { eventData } = useHistorySearch();
  const map = useMap();

  const hoveredEvent = useMemo(() => {
    if (!hoveredEventId) return;

    const visibleItems = eventData.filter((item) => {
      if (visibleJourneyIds && !visibleJourneyIds.includes(item.journeyId)) return false;
      if (visibleTripId && item.tripId !== visibleTripId) return false;
      return true;
    });

    return visibleItems
      .flatMap((item) => item.data.items as EventLocationData[])
      .find((event) => event.eventId === hoveredEventId);
  }, [eventData, hoveredEventId, visibleJourneyIds, visibleTripId]);

  const hoveredEventPoint = useMemo(() => getEventLatLng(hoveredEvent), [hoveredEvent]);
  const eventColor = useMemo(() => hoveredEvent ? getEventColor(hoveredEvent, eventDefinitions) : '#1E75D8', [eventDefinitions, hoveredEvent]);

  const resolveRenderedMarkerPoint = useCallback((): L.LatLng | undefined => {
    if (!hoveredEventId) return;

    let point: L.LatLng | undefined;
    map.eachLayer((layer) => {
      if (!(layer instanceof L.Marker)) return;

      const markerEventId = layer.options.title;
      if (markerEventId !== hoveredEventId) return;

      point = layer.getLatLng();
    });

    return point;
  }, [hoveredEventId, map]);

  const [renderedMarkerPoint, setRenderedMarkerPoint] = useState<L.LatLng | undefined>();

  useEffect(() => {
    const updateRenderedMarkerPoint = (): void => {
      setRenderedMarkerPoint((currentPoint) => {
        const nextPoint = resolveRenderedMarkerPoint();
        if ((!currentPoint && !nextPoint) || (currentPoint && nextPoint && currentPoint.equals(nextPoint))) return currentPoint;
        return nextPoint;
      });
    };

    updateRenderedMarkerPoint();

    // cspell:ignore moveend layeradd layerremove
    map.on('moveend zoomend layeradd layerremove', updateRenderedMarkerPoint);
    return () => {
      map.off('moveend zoomend layeradd layerremove', updateRenderedMarkerPoint);
    };
  }, [map, resolveRenderedMarkerPoint]);

  const pulsePosition = renderedMarkerPoint ?? hoveredEventPoint;

  const pulseIcon = useMemo(() => {
    const pulseShadowSize = 10;
    return L.divIcon({
      className: 'trip-history-hover-pulse-marker',
      html: [
        '<div style="position:relative;width:16px;height:16px;pointer-events:none;">',
        '<style>@keyframes trip-history-hover-pulse{0%{transform:scale(0.9);box-shadow:0 0 0 0 ',
        eventColor,
        ';}70%{transform:scale(1);box-shadow:0 0 0 ',
        pulseShadowSize,
        'px ',
        `${eventColor}00`,
        ';}100%{transform:scale(0.9);box-shadow:0 0 0 0 ',
        `${eventColor}00`,
        ';}}</style>',
        '<div style="width:16px;height:16px;border-radius:50%;background:',
        eventColor,
        ';box-shadow:0 0 0 0 ',
        eventColor,
        ';animation:trip-history-hover-pulse 2000ms infinite;"></div>',
        '</div>',
      ].join(''),
      iconAnchor: [8, 8],
      iconSize: [16, 16],
    });
  }, [eventColor]);

  if (!pulsePosition || !hoveredEvent?.eventId) return <></>;

  return <Marker icon={pulseIcon} interactive={false} keyboard={false} position={pulsePosition} zIndexOffset={1200} />;
};
